import { useNavigate } from 'react-router-dom'
import { useState } from 'react'
import posthog from 'posthog-js'
import { FiChevronLeft } from 'react-icons/fi'
import { faPlain } from 'utilities/helper/format'

const VISIBLE = 3

/**
 * «صندوق‌هایی که مدیر آن هستید» — a door to داشبورد مدیریت.
 *
 * Interim version: navigation only, derived client-side from the already
 * cached `['get-boxes']` payload, which carries `poolship.role` for every
 * fund. The work signals (how many members are behind, how many loan requests
 * are waiting) need the new `managed-summary` endpoint and land in the next MR.
 *
 * Hard rule, whatever ships later: never call `admin-dashboard/stats/` per
 * managed fund from here. It runs ~4s on the largest fund, so a manager of
 * three funds would 504 on every app open.
 */
export default function ManagedFunds({
    moneyPools,
}: {
    moneyPools: MoneyPool[]
}) {
    const navigate = useNavigate()
    const [expanded, setExpanded] = useState(false)

    // Funds you OWN come first, then ones you merely manage; within each, the
    // biggest fund first. Ownership outranks size because it is where the
    // decisions actually sit, and member count is the best proxy we have here
    // for "which of these matters most" until managed-summary lands with real
    // work signals.
    const managed = moneyPools
        .filter((mp) => ['own', 'mgr'].includes(mp.poolship?.role))
        .sort((a, b) => {
            const rank = (mp: MoneyPool) => (mp.poolship?.role === 'own' ? 0 : 1)
            if (rank(a) !== rank(b)) return rank(a) - rank(b)
            return (b.active_members_count ?? 0) - (a.active_members_count ?? 0)
        })
    if (managed.length === 0) return null

    const shown = expanded ? managed : managed.slice(0, VISIBLE)
    const hidden = managed.length - shown.length

    const open = (mp: MoneyPool) => {
        posthog.capture('admin_dashboard_opened', {
            source: 'home',
            moneypool_id: mp.id,
        })
        navigate(`/fund/admin-dashboard/${mp.id}`)
    }

    return (
        <div>
            <p className="text-sm font-bold text-text-secondary text-right mb-2 px-1">
                صندوق‌هایی که مدیر آن هستید
            </p>
            <div className="bg-white border border-divider rounded-card overflow-hidden">
                {shown.map((mp, i) => (
                    <button
                        type="button"
                        key={mp.id}
                        onClick={() => open(mp)}
                        className={`w-full flex items-center gap-2 px-4 py-3 text-right cursor-pointer ${
                            i > 0 ? 'border-t border-divider' : ''
                        }`}
                    >
                        <span className="flex-1 min-w-0">
                            <span className="block text-sm text-heading font-bold truncate">
                                {mp.name}
                            </span>
                            <span className="block text-xs text-text-secondary">
                                {faPlain.format(mp.active_members_count ?? 0)} عضو
                                {mp.poolship?.role === 'own' && ' · مالک'}
                            </span>
                        </span>
                        <FiChevronLeft className="text-text-muted shrink-0" />
                    </button>
                ))}
                {hidden > 0 && (
                    <button
                        type="button"
                        onClick={() => setExpanded(true)}
                        className="w-full border-t border-divider py-2 text-xs text-green-1 font-bold cursor-pointer"
                    >
                        {faPlain.format(hidden)} صندوق دیگر
                    </button>
                )}
            </div>
        </div>
    )
}
